import * as React from 'react';

import { InfoIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Divider, Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe, Undef } from 'types/UtilityTypes';
import { BuyRateData, SellRateData } from 'types/agents/QuickQuoteAgent';

import { formatDollarsNoCents } from '../utils';
import {
  BuyRateQuoteRangeRow,
  BuyRateRangePercentileRuleBlock,
  BuyRateRuleRow,
  getPercentileRangeSelectionInline,
} from './BuyRateRuleRow';
import { RoundingRulesNote } from './RoundingRulesNote';
import { SellRateRuleBreakdown } from './SellRateRuleBreakdown';
import {
  type BuyRateBreakdownState,
  getBuyRateBreakdownState,
  getDistanceSourceDisplay,
  getFixedFinalRuleDisplay,
  getMarginDisplay,
  getQuoteSourceDisplay,
  getTransportTypeDisplay,
  isFixedFinalPricing,
} from './helpers';

interface SingleLaneDescriptionProps {
  buyRateData?: Maybe<BuyRateData>;
  sellRateData?: SellRateData;
}

interface FixedFinalDescriptionProps {
  buyRateData?: Maybe<BuyRateData>;
  sellRateData: SellRateData;
  appliedSellRateRule?: SellRateData['appliedSellRateRule'];
}

interface BuyRateTooltipContentProps {
  originalBuyRate: Undef<number>;
  quoteSourceDisplay: string;
  buyRateRangeLow: Undef<number>;
  buyRateRangeHigh: Undef<number>;
  buyRate: Undef<number>;
  appliedRules: NonNullable<BuyRateData['appliedRules']>;
  breakdownState: BuyRateBreakdownState;
}

const FixedFinalDescription = ({
  buyRateData,
  sellRateData,
  appliedSellRateRule,
}: FixedFinalDescriptionProps): React.JSX.Element => {
  const fixedFinalRuleDisplay = getFixedFinalRuleDisplay(sellRateData);
  const distanceSourceDisplay = getDistanceSourceDisplay(
    buyRateData?.distanceSource
  );

  return (
    <Flex direction='col' gap='xs'>
      <Typography variant='body-xs'>
        {'Drumkit calculated a quote using '}
        <Typography variant='body-xs' weight='semibold' as='span'>
          {fixedFinalRuleDisplay}
        </Typography>
        {distanceSourceDisplay && ` with ${distanceSourceDisplay} distance`}
        {'.'}
      </Typography>

      <Flex direction='col' gap='xs' className='w-full mt-1 max-w-sm'>
        <SellRateRuleBreakdown
          sellRate={sellRateData.sellRate}
          sellRateRounded={sellRateData.sellRateRounded}
          appliedSellRateRule={appliedSellRateRule}
          marginType={sellRateData.marginType}
          marginValue={sellRateData.marginValue}
          pricingFormula={sellRateData.pricingFormula}
          pricingMode={sellRateData.pricingMode}
          roundingRules={sellRateData.sellRateRoundingRules}
        />
      </Flex>
    </Flex>
  );
};

const BuyRateTooltipContent = ({
  originalBuyRate,
  quoteSourceDisplay,
  buyRateRangeLow,
  buyRateRangeHigh,
  buyRate,
  appliedRules,
  breakdownState,
}: BuyRateTooltipContentProps): Maybe<React.JSX.Element> => {
  if (!breakdownState.hasBuyRateBreakdown) {
    return null;
  }

  return (
    <Tooltip delayDuration={10}>
      <TooltipTrigger asChild>
        <InfoIcon className='w-3 h-3 cursor-help' />
      </TooltipTrigger>
      <TooltipContent className='w-72 p-2 ml-2'>
        <Flex direction='col' gap='xs'>
          {originalBuyRate != null && (
            <Flex align='center' justify='between' className='w-full gap-3'>
              <Typography
                variant='body-xxs'
                textColor='lessMuted'
                className='min-w-0 flex-1 leading-snug'
              >
                {'Original '}
                {quoteSourceDisplay !== '—' ? `${quoteSourceDisplay} ` : ''}
                Rate Estimate
              </Typography>
              <Typography
                variant='body-xxs'
                weight='medium'
                className='shrink-0 tabular-nums'
              >
                {formatDollarsNoCents(originalBuyRate)}
              </Typography>
            </Flex>
          )}
          {breakdownState.showStandaloneQuoteRange && (
            <BuyRateQuoteRangeRow
              low={buyRateRangeLow}
              high={buyRateRangeHigh}
              targetBuyRate={
                breakdownState.showMetadataTargetBuyInRange
                  ? buyRate
                  : undefined
              }
            />
          )}
          {appliedRules.map((rule, idx) =>
            rule.adjustmentType === 'rangePercentile' ? (
              <BuyRateRangePercentileRuleBlock
                key={idx}
                rule={rule}
                rangeLow={buyRateRangeLow}
                rangeHigh={buyRateRangeHigh}
              />
            ) : (
              <BuyRateRuleRow key={idx} rule={rule} />
            )
          )}
          {breakdownState.showFinalBuyRateRow && (
            <>
              <Divider className='my-0.5' />
              <Flex align='center' justify='between' className='w-full'>
                <Typography variant='body-xxs' weight='semibold'>
                  Final Buy Rate
                </Typography>
                <Typography variant='body-xxs' weight='semibold'>
                  {formatDollarsNoCents(buyRate)}
                </Typography>
              </Flex>
            </>
          )}
        </Flex>
      </TooltipContent>
    </Tooltip>
  );
};

export const SingleLaneDescription = ({
  buyRateData,
  sellRateData,
}: SingleLaneDescriptionProps): Maybe<React.JSX.Element> => {
  const appliedSellRateRule = sellRateData?.appliedSellRateRule;
  const isFixedFinal = isFixedFinalPricing(sellRateData);
  if (isFixedFinal && sellRateData) {
    return (
      <FixedFinalDescription
        buyRateData={buyRateData}
        sellRateData={sellRateData}
        appliedSellRateRule={appliedSellRateRule}
      />
    );
  }

  if (!buyRateData) {
    return null;
  }

  const sellRulePricingDisplay = appliedSellRateRule
    ? (getMarginDisplay(appliedSellRateRule) ?? '')
    : null;
  const fallbackMarginDisplay = getMarginDisplay(sellRateData) ?? '';
  const marginDisplay = sellRulePricingDisplay ?? fallbackMarginDisplay;
  const hasSellRateData = Boolean(sellRateData);
  const transportTypeDisplay = getTransportTypeDisplay(
    buyRateData.transportType
  );
  const quoteSourceDisplay = getQuoteSourceDisplay(
    buyRateData.selectedQuoteSource
  );
  const buyRate = buyRateData.buyRate;
  const originalBuyRate = buyRateData.originalBuyRate;
  const buyRateRangeLow = buyRateData.buyRateRangeLow;
  const buyRateRangeHigh = buyRateData.buyRateRangeHigh;
  const appliedRules = buyRateData.appliedRules ?? [];
  const buyRateBreakdownState = getBuyRateBreakdownState(
    appliedRules,
    buyRateRangeLow,
    buyRateRangeHigh
  );

  const hasRulesApplied =
    appliedRules.length > 0 &&
    originalBuyRate != null &&
    originalBuyRate !== buyRate;

  const percentileRules = appliedRules.filter(
    (r) => r.adjustmentType === 'rangePercentile'
  );
  const singleRangePercentileRule =
    appliedRules.length === 1 && percentileRules.length === 1
      ? percentileRules[0]
      : undefined;

  return (
    <Flex direction='col' gap={hasSellRateData ? 'xs' : 'none'}>
      <Typography variant='body-xs'>
        {'Drumkit found a '}
        <Typography variant='body-xs' weight='semibold' as='span'>
          {formatDollarsNoCents(originalBuyRate ?? buyRate)}
        </Typography>{' '}
        <Typography variant='body-xs' weight='semibold' as='span'>
          {transportTypeDisplay}
        </Typography>{' '}
        {'quote from '}
        <Typography variant='body-xs' weight='semibold' as='span'>
          {quoteSourceDisplay}
        </Typography>
        {hasRulesApplied && (
          <>
            {', '}
            {singleRangePercentileRule ? (
              <Typography variant='body-xs' weight='semibold' as='span'>
                {getPercentileRangeSelectionInline(
                  singleRangePercentileRule.adjustmentValue
                )}
              </Typography>
            ) : percentileRules.length > 0 ? (
              <>
                {'applied '}
                <Typography variant='body-xs' weight='semibold' as='span'>
                  {appliedRules.length} buy rate rule
                  {appliedRules.length !== 1 ? 's' : ''}
                </Typography>
                {' including a percentile selection within the quote range'}
              </>
            ) : (
              <>
                {'applied '}
                <Typography variant='body-xs' weight='semibold' as='span'>
                  {appliedRules.length} buy rate rule
                  {appliedRules.length !== 1 ? 's' : ''}
                </Typography>
              </>
            )}
          </>
        )}
        {hasSellRateData && (
          <>
            {', '}
            {appliedSellRateRule ? (
              <>
                {'applied sell rate rule '}
                <Typography variant='body-xs' weight='semibold' as='span'>
                  {appliedSellRateRule.ruleName
                    ? `"${appliedSellRateRule.ruleName}" `
                    : 'override '}
                </Typography>
                (
                <Typography variant='body-xs' weight='semibold' as='span'>
                  {marginDisplay}
                </Typography>
                )
              </>
            ) : (
              <>
                {'applied a '}
                <Typography variant='body-xs' weight='semibold' as='span'>
                  {marginDisplay}
                </Typography>
              </>
            )}
          </>
        )}
        {sellRateData?.sellRateRoundingRules?.roundToNearest && ' and rounded'}
      </Typography>

      <Flex direction='col' gap='xs' className='w-full mt-1'>
        <Flex gap='md' className='w-full'>
          <Flex direction='col' gap='xs'>
            <Typography
              variant='body-xxs'
              textColor='lessMuted'
              className='flex items-center gap-1'
            >
              Buy Rate
              <BuyRateTooltipContent
                originalBuyRate={originalBuyRate}
                quoteSourceDisplay={quoteSourceDisplay}
                buyRateRangeLow={buyRateRangeLow}
                buyRateRangeHigh={buyRateRangeHigh}
                buyRate={buyRate}
                appliedRules={appliedRules}
                breakdownState={buyRateBreakdownState}
              />
            </Typography>
            <Typography variant='body-xs' weight='semibold'>
              {formatDollarsNoCents(buyRate)}
            </Typography>
          </Flex>
          {hasSellRateData && sellRateData && (
            <Flex direction='col' gap='xs'>
              <Typography
                variant='body-xxs'
                textColor='lessMuted'
                className='flex items-center gap-1'
              >
                Sell Rate
                <Tooltip delayDuration={10}>
                  <TooltipTrigger asChild>
                    <InfoIcon className='w-3 h-3 cursor-help' />
                  </TooltipTrigger>
                  <TooltipContent className='w-72 p-2 ml-2'>
                    <SellRateRuleBreakdown
                      buyRate={buyRate}
                      sellRate={sellRateData.sellRate}
                      sellRateRounded={sellRateData.sellRateRounded}
                      appliedSellRateRule={appliedSellRateRule}
                      marginType={sellRateData.marginType}
                      marginValue={sellRateData.marginValue}
                      pricingFormula={sellRateData.pricingFormula}
                      pricingMode={sellRateData.pricingMode}
                      roundingRules={sellRateData.sellRateRoundingRules}
                    />
                  </TooltipContent>
                </Tooltip>
              </Typography>
              <Typography variant='body-xs' weight='semibold'>
                {formatDollarsNoCents(
                  sellRateData.sellRateRounded !== undefined
                    ? sellRateData.sellRateRounded
                    : sellRateData.sellRate
                )}
              </Typography>
            </Flex>
          )}
        </Flex>
        {hasSellRateData && sellRateData && (
          <RoundingRulesNote rules={sellRateData.sellRateRoundingRules} />
        )}
      </Flex>
    </Flex>
  );
};
